import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Select, Table, Tag} from "antd";
import {EditOutlined, EyeOutlined, PlusOutlined} from "@ant-design/icons";
import {observer} from "mobx-react-lite";
import {complexesModel} from "../../../../../entities/complexes";
import {config} from "../../../../../shared/config/config";
import {SectionToolbar} from "../../components/SectionToolbar/SectionToolbar";
import {ComplexEditor} from "./ComplexEditor";
import {useCanEdit} from "../../../sectionAccess";

// the portal lives next to the API: portalagentam.ru
const PORTAL = config.API_HOST

const MISSING = [
  {key: 'description', label: 'Без описания', tag: 'нет описания'},
  {key: 'photo', label: 'Без фото', tag: 'нет фото'},
  {key: 'address', label: 'Без адреса или точки на карте', tag: 'нет адреса / точки'},
  {key: 'deadline', label: 'Без срока сдачи', tag: 'нет срока'},
  {key: 'developer', label: 'Без застройщика', tag: 'нет застройщика'},
  {key: 'curator', label: 'Без куратора', tag: null},
]

const imageUrl = (file) => `${config.API_HOST}/media/${String(file).split('/').map(encodeURIComponent).join('/')}`

const columnsFor = (openEditor, canEdit) => [
  {
    title: 'Фото',
    key: 'photo',
    width: 84,
    render: ({id, complexImage, name}) => (
      <button type='button' className='complex-thumb-button' onClick={() => openEditor(id)} aria-label={`Изменить ${name}`}>
        {complexImage
          ? <img className='complex-thumb' src={imageUrl(complexImage)} alt={name} loading='lazy'/>
          : <div className='complex-thumb complex-thumb--empty'>нет фото</div>}
      </button>
    ),
  },
  {
    title: 'ЖК',
    key: 'name',
    render: ({id, name, sameType}) => (
      <>
        <a className='complex-name' href={`${PORTAL}/residential-complexes/${id}`} target='_blank' rel='noopener noreferrer'>{name}</a>
        {sameType && <div className='cell-hint'>{sameType}</div>}
      </>
    ),
  },
  {title: 'Застройщик', key: 'developer', render: ({developer}) => developer || <span className='cell-empty'>—</span>},
  {
    title: 'Адрес и метро',
    key: 'address',
    render: ({address, metroName, distanceToMetro}) => (
      <>
        <div>{address || <span className='cell-empty'>—</span>}</div>
        {metroName && <div className='cell-hint'>м. {metroName}{distanceToMetro ? `, ${distanceToMetro}` : ''}</div>}
      </>
    ),
  },
  {title: 'Срок сдачи', key: 'deadline', render: ({deadline}) => deadline || <span className='cell-empty'>—</span>},
  {
    title: 'Квартиры',
    key: 'flats',
    align: 'right',
    render: ({onSale, total}) => (
      <div style={{whiteSpace: 'nowrap'}}>
        <b>{onSale}</b> в продаже
        <div className='cell-hint'>из {total}</div>
      </div>
    ),
  },
  {title: 'Куратор', key: 'curator', render: ({curatorName}) => curatorName || <span className='cell-empty'>не назначен</span>},
  {
    title: 'Карточка',
    key: 'missing',
    render: ({missing}) => {
      const tags = MISSING.filter((m) => m.tag && missing.includes(m.key))
      return tags.length
        ? <div className='tag-list'>{tags.map((m) => <Tag key={m.key} color='red'>{m.tag}</Tag>)}</div>
        : <Tag color='green'>заполнена</Tag>
    },
  },
  {
    title: '',
    key: 'edit',
    width: 120,
    fixed: 'right',
    render: ({id}) => <Button icon={canEdit ? <EditOutlined/> : <EyeOutlined/>} onClick={() => openEditor(id)}>{canEdit ? 'Изменить' : 'Открыть'}</Button>,
  },
]

export const ComplexesSection = observer(() => {
  const canEdit = useCanEdit()
  const [search, setSearch] = useState(complexesModel.search)
  // the card open in the editor: an id, 'new' or null
  const [editing, setEditing] = useState(null)
  const timer = useRef(null)

  useEffect(() => {
    complexesModel.fetch()
    return () => clearTimeout(timer.current)
  }, [])

  const onSearch = (value) => {
    setSearch(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => complexesModel.setFilters({search: value.trim()}), 400)
  }

  // read here, not only inside antd's title render, so the toolbar redraws on every change
  const {missingCounts: counts, missing, stock, sort, total} = complexesModel

  return (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message='«Изменить» или фото открывают карточку ЖК: описание, фото, адрес, куратор, документы. Название открывает страницу ЖК на портале. Пустые поля ночью дозаполняются из квартир ЖК и со старого сайта.'
      />
      {complexesModel.error && <Alert className='section-note' type='error' message={complexesModel.error}/>}
      <Table
        title={() => (
          <SectionToolbar
            search={search}
            onSearch={onSearch}
            placeholder='Название, застройщик, адрес или метро'
            count={`Всего ${total}`}
            filters={(
              <>
                <Select
                  allowClear
                  style={{width: 250}}
                  placeholder='Чего не хватает в карточке'
                  value={missing}
                  onChange={(value) => complexesModel.setFilters({missing: value})}
                >
                  {MISSING.map((m) => (
                    <Select.Option key={m.key} value={m.key}>
                      {m.label}{counts[m.key] !== undefined ? ` (${counts[m.key]})` : ''}
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  allowClear
                  style={{width: 200}}
                  placeholder='Все ЖК'
                  value={stock}
                  onChange={(value) => complexesModel.setFilters({stock: value})}
                >
                  <Select.Option value='onSale'>Есть квартиры в продаже</Select.Option>
                  <Select.Option value='none'>Нет квартир в продаже</Select.Option>
                </Select>
                <Select style={{width: 220}} value={sort} onChange={(value) => complexesModel.setFilters({sort: value})}>
                  <Select.Option value='onSale'>Больше квартир в продаже</Select.Option>
                  <Select.Option value='name'>По названию</Select.Option>
                  <Select.Option value='updatedAt'>Недавно изменённые</Select.Option>
                  <Select.Option value='missing'>Меньше всего заполнено</Select.Option>
                </Select>
              </>
            )}
          >
            {canEdit && <Button type='primary' icon={<PlusOutlined/>} onClick={() => setEditing('new')}>Добавить ЖК</Button>}
          </SectionToolbar>
        )}
        columns={columnsFor(setEditing, canEdit)}
        dataSource={complexesModel.complexes}
        rowKey='id'
        loading={complexesModel.isLoading}
        locale={{emptyText: 'Ничего не нашлось'}}
        sticky
        scroll={{x: 1220}}
        pagination={{
          current: complexesModel.page,
          pageSize: complexesModel.limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          locale: {items_per_page: '/ стр.'},
          showTotal: (total, [from, to]) => `${from}–${to} из ${total}`,
          onChange: (page, pageSize) => complexesModel.setPage(page, pageSize),
        }}
      />
      {editing !== null && (
        <ComplexEditor
          key={editing}
          complexId={editing}
          readOnly={!canEdit}
          onClose={() => setEditing(null)}
          onSaved={() => complexesModel.fetch()}
          onCreated={(card) => {
            complexesModel.fetch()
            setEditing(card.id)
          }}
          onDeleted={() => {
            setEditing(null)
            complexesModel.fetch()
          }}
        />
      )}
    </>
  )
})
